import { FunctionalComponent } from 'preact'
import { Globe, Loader2, Lock, Plus, X } from 'lucide-preact'
import { TextBox } from '@/components'
import {
  NON_PROSPECT_DOMAINS_MAX_COUNT,
  type InheritedNonProspectDomain,
  type InheritedNonProspectDomainSource,
  type NonProspectDomainsViewDto,
} from '@/types/nonProspectDomains'
import type { ApiResponse } from '@/api/client'
import { useNonProspectDomainsEditor, type NonProspectDomainsEditor } from './useNonProspectDomainsEditor'

interface NonProspectDomainsPanelProps {
  view: NonProspectDomainsViewDto
  onSave: (domains: string[]) => Promise<ApiResponse<NonProspectDomainsViewDto> | null>
  /** Copy differs between the standing company list and a single workspace's list. */
  level: 'company' | 'workspace'
  readOnly?: boolean
}

// Ring-style source badges, matching the ApprovedDomainsPanel convention.
const INHERITED_BADGE: Record<InheritedNonProspectDomainSource, { label: string; className: string }> = {
  client_company: {
    label: 'Company',
    className: 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 ring-sky-600/20',
  },
  managing_company: {
    label: 'Managing company',
    className: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 ring-violet-600/20',
  },
}

const OWN_BADGE =
  'text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/60 ring-slate-300/60 dark:ring-slate-600/60'

const COPY = {
  company: {
    blurb:
      'People at these email domains are still recorded, but are not counted or shown as prospects. Use it for your own staff and for this customer’s. Applies to every campaign and event.',
    footer:
      'Removing a domain brings those people straight back into the audience views — no re-import needed.',
  },
  workspace: {
    blurb:
      'Extra domains that are not prospects in this workspace — a brand or division that shares the account but should not be counted. Domains inherited from the company apply here too and are managed there.',
    footer:
      'These apply to every campaign and event in this workspace, and to the workspace stats. Other workspaces are unaffected.',
  },
} as const

function SourceBadge({ label, className }: { label: string; className: string }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}>
      {label}
    </span>
  )
}

function InheritedRow({ domain }: { domain: InheritedNonProspectDomain }) {
  const badge = INHERITED_BADGE[domain.Source]
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Globe className="w-4 h-4 shrink-0 text-slate-400" />
      <span className="flex-1 min-w-0 truncate font-medium text-slate-900 dark:text-white" title={domain.Host}>
        {domain.Host}
      </span>
      {badge && (
        <SourceBadge
          label={domain.SourceName ? `${badge.label}: ${domain.SourceName}` : badge.label}
          className={badge.className}
        />
      )}
      <Lock className="w-3.5 h-3.5 shrink-0 text-slate-400" aria-label="Read-only" />
    </li>
  )
}

function OwnRow({
  domain,
  isRemoving,
  disabled,
  onRemove,
}: {
  domain: string
  isRemoving: boolean
  disabled: boolean
  onRemove: () => void
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Globe className="w-4 h-4 shrink-0 text-slate-400" />
      <span className="flex-1 min-w-0 truncate font-medium text-slate-900 dark:text-white" title={domain}>
        {domain}
      </span>
      <SourceBadge label="Added here" className={OWN_BADGE} />
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${domain}`}
        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
      >
        {isRemoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
      </button>
    </li>
  )
}

function AddDomainForm({ editor }: { editor: NonProspectDomainsEditor }) {
  const { newDomain, error, saving, setNewDomain, clearError, handleAdd, closeAddForm } = editor
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2">
      <div className="flex-1 min-w-0">
        <TextBox
          name="new-non-prospect-domain"
          value={newDomain}
          onChange={(next) => {
            setNewDomain(next)
            clearError()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !saving) void handleAdd()
            if (e.key === 'Escape') closeAddForm()
          }}
          error={error ?? undefined}
          placeholder="example.com"
          density="compact"
          disabled={saving}
          autoFocus
        />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => {
            void handleAdd()
          }}
          disabled={saving || !newDomain.trim()}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add
        </button>
        <button
          type="button"
          onClick={closeAddForm}
          className="px-3 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  )
}

/**
 * The non-prospect domain editor (ORB-619), used at both the client-company and web levels.
 *
 * Mirrors ApprovedDomainsPanel: inherited entries render first, read-only with a badge naming the
 * level that owns them, then this level's own entries with add/remove. Each mutation persists
 * immediately, so there is no half-saved state. State, validation and save orchestration live in
 * useNonProspectDomainsEditor; this component is presentation only.
 */
export const NonProspectDomainsPanel: FunctionalComponent<NonProspectDomainsPanelProps> = ({
  view,
  onSave,
  level,
  readOnly = false,
}) => {
  const editor = useNonProspectDomainsEditor(view, onSave)
  const { inherited, own, adding, saving, removing, atLimit, openAddForm } = editor
  const copy = COPY[level]

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Non-Prospect Domains</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 max-w-prose">{copy.blurb}</p>
      </div>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900">
        {inherited.map((domain) => (
          <InheritedRow key={`inherited:${domain.Source}:${domain.Host}`} domain={domain} />
        ))}

        {own.map((domain) => (
          <OwnRow
            key={`own:${domain}`}
            domain={domain}
            isRemoving={removing === domain}
            // Disabled while ANY mutation is in flight: the list is replaced wholesale against a
            // single ETag, so a second concurrent PATCH would 412.
            disabled={readOnly || saving}
            onRemove={() => {
              void editor.handleRemove(domain)
            }}
          />
        ))}

        {inherited.length === 0 && own.length === 0 && (
          <li className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            No non-prospect domains yet. Everyone counts as a prospect.
          </li>
        )}
      </ul>

      {!readOnly &&
        (adding ? (
          <AddDomainForm editor={editor} />
        ) : (
          <button
            type="button"
            onClick={openAddForm}
            disabled={atLimit || saving}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={atLimit ? `Limit of ${NON_PROSPECT_DOMAINS_MAX_COUNT} non-prospect domains reached` : undefined}
          >
            <Plus className="w-4 h-4" /> Add domain
          </button>
        ))}

      <p className="text-xs text-slate-400 dark:text-slate-500 max-w-prose leading-relaxed">{copy.footer}</p>
    </div>
  )
}
